import { ChangeDetectionStrategy, Component, OnInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BotInfo, BotInfoListItem } from "../../../../shared/_types/bot-info.type";
import { MarketplaceService } from "./marketplace.service";
import { NotificationsService } from "../../../../shared/_services/notifications.service";
import { CommandResult } from "../../../../shared/_types/command-result.type";
import { MatDialog } from "@angular/material/dialog";
import {
    ConfirmDialogComponent,
    ConfirmDialogData,
} from "../../../../shared/_components/confirm-dialog/confirm-dialog.component";

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-marketplace",
    templateUrl: "./marketplace.component.html",
    styleUrl: "./marketplace.component.scss",
    standalone: false,
})
export class MarketplaceComponent implements OnInit, OnDestroy {
    botInfos: BotInfoListItem[] = [];
    displayedColumns: string[] = ["name", "isPublished", "lastUpdatedAtUtc", "shortDescription", "actions"];
    loading = false;
    private ngUnsubscribe = new Subject<void>();

    constructor(
        private marketplaceService: MarketplaceService,
        private router: Router,
        private notificationsService: NotificationsService,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.loadBotInfos();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    loadBotInfos(): void {
        this.loading = true;
        this.marketplaceService
            .list()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (botInfos) => {
                    this.botInfos = botInfos;
                    this.loading = false;
                },
                error: () => {
                    this.notificationsService.showError("Error", "Failed to load bot infos");
                    this.loading = false;
                },
            });
    }

    createNew(): void {
        this.router.navigate(["/marketplace/new"]);
    }

    editBotInfo(id: string): void {
        this.router.navigate(["/marketplace", id]);
    }

    togglePublish(botInfo: BotInfo): void {
        const operation = botInfo.isPublished
            ? this.marketplaceService.unpublish(botInfo.id)
            : this.marketplaceService.publish(botInfo.id);

        operation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
            next: (updatedBotInfo) => {
                const index = this.botInfos.findIndex((b) => b.id === updatedBotInfo.id);
                if (index !== -1) {
                    this.botInfos[index].isPublished = updatedBotInfo.isPublished;
                }
                const action = updatedBotInfo.isPublished ? "published" : "unpublished";
                this.notificationsService.showSuccess("Success", `Bot info ${action} successfully`);
            },
            error: () => {
                this.notificationsService.showError("Error", "Failed to update bot info status");
            },
        });
    }

    deleteBotInfo(botInfo: BotInfo): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: "Delete Bot Info",
                message: `Are you sure you want to delete "${botInfo.name}"?`,
                type: "danger",
            } as ConfirmDialogData,
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            this.marketplaceService
                .delete(botInfo.id)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe({
                    next: (result: CommandResult) => {
                        if (result.success) {
                            this.botInfos = this.botInfos.filter((b) => b.id !== botInfo.id);
                            this.notificationsService.showSuccess("Success", "Bot info deleted successfully");
                        } else {
                            this.notificationsService.showError("Error", result.error || "Failed to delete bot info");
                        }
                    },
                    error: () => {
                        this.notificationsService.showError("Error", "Failed to delete bot info");
                    },
                });
        });
    }

    formatDate(date: Date | string): string {
        if (!date) {
            return "";
        }
        const d = new Date(date);
        return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
}
